import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'navigation/types';

import { ApiDocumentSource } from '@izimi/api';
import { useDocumentFiltering, useModalStore, useTracking, withMessages } from '@izimi/components';
import {
  appEnv,
  createExtendedDocumentsModule,
  useDocumentDeleteStore,
  useDocumentSorting,
} from '@izimi/core';
import { useDocumentSelection, useQueryParams } from '@izimi/hooks';
import type { DocumentEntity, PagedDocumentsArgs } from '@izimi/modules';
import { createDocumentsModule, getDocumentFilterParameters, Tracker } from '@izimi/modules';
import { getRouteWithParams } from '@izimi/utils';
import { useConnectivePlugin } from 'core/connectivePlugin/useConnectivePlugin';
import { documentsApi } from 'core/di';
import { isNotaryFilter } from 'pages/vault/components/NotaryFilters';

import documentsMessages from './messages';

const hooks = createDocumentsModule({ documentsApi });
const extendedHooks = createExtendedDocumentsModule({ documentsApi });

const useHandleDeleteDocuments = () => {
  const { moveDocumentsToTrash, isPending } = hooks.useMoveDocumentsToTrash();

  const handleDeleteDocuments = useCallback(
    async (documents: DocumentEntity[]) => {
      const documentIds = documents.map((d) => d.id);
      await withMessages(moveDocumentsToTrash(documentIds))(
        documentsMessages.trashDocuments({
          numberOfDocs: documents.length,
          fileName: documents[0]?.name,
        }),
      );
    },
    [moveDocumentsToTrash],
  );

  return {
    handleDeleteDocuments,
    isPending,
  };
};

const useDocumentOptions = () => {
  const { showModal, hideModal } = useModalStore();
  const { hideDeleteDocumentConfirmation } = useDocumentDeleteStore();
  const { downloadDocumentToFileSystem, isPending: isLoadingDownloadDocument } =
    extendedHooks.useDownloadDocumentToFileSystem();
  const { copyDocument, isPending: isLoadingCopyDocument } = hooks.useCopyDocument();
  const { handleDeleteDocuments, isPending: isLoadingDeleteDocument } = useHandleDeleteDocuments();
  const { markDocumentAsSeen } = hooks.useMarkDocumentAsSeen();
  const { activeDocumentId, clearDocumentSelection } = useDocumentSelection();
  const { trackEvent } = useTracking();
  const navigate = useNavigate();
  const { initialize: initializeConnectivePlugin } = useConnectivePlugin();

  const handleDeleteDocument = useCallback(
    async (document: DocumentEntity) => {
      if (hideDeleteDocumentConfirmation) {
        trackEvent({
          category: Tracker.Category.Vault,
          action: Tracker.Action.RemovedDocument,
          value: 1,
        });
        await handleDeleteDocuments([document]);
        if (document.id === activeDocumentId) {
          clearDocumentSelection();
        }
        hideModal();
      } else {
        showModal({ type: 'deleteDocuments', documents: [document] });
      }
    },
    [
      activeDocumentId,
      clearDocumentSelection,
      handleDeleteDocuments,
      hideDeleteDocumentConfirmation,
      showModal,
      hideModal,
      trackEvent,
    ],
  );

  const handleEditDocumentName = useCallback(
    (document: DocumentEntity) => {
      showModal({ type: 'editDocumentName', document });
    },
    [showModal],
  );

  const handleEditDocumentLabels = useCallback(
    (document: DocumentEntity) => {
      showModal({ type: 'editDocumentLabels', document });
    },
    [showModal],
  );

  const handleCopyDocument = useCallback(
    async (document: DocumentEntity) => {
      await withMessages(copyDocument(document.id))(documentsMessages.copyDocument(document.name));
      hideModal();
    },
    [copyDocument, hideModal],
  );

  const handleDocumentHistory = useCallback(
    (document: DocumentEntity) => {
      showModal({ type: 'documentHistoryLog', document });
    },
    [showModal],
  );

  const handleSendCommunication = useCallback(
    (document: DocumentEntity) => {
      showModal({ type: 'sendCommunication', documents: [document] });
    },
    [showModal],
  );

  const handleDocumentSeen = useCallback(
    (documents: Array<DocumentEntity>, isSeen: boolean) => {
      markDocumentAsSeen({
        documentIds: documents.map((d) => d.id),
        isSeen,
      });
      hideModal();
    },
    [markDocumentAsSeen, hideModal],
  );

  const handleCertifyDocument = useCallback(
    async (document: DocumentEntity) => {
      // Currently we test the limit everywhere except on test env
      // Temporary solution to prevent users from certifying documents (>=22mb) that the backend can't handle
      if (appEnv !== 'test' && document.size >= 22 * 1024 * 1024) {
        showModal({ type: 'tooBigToCertify' });
      } else {
        try {
          await initializeConnectivePlugin();
          navigate(
            getRouteWithParams({
              route: Routes.Certification,
              params: { documentId: document.id },
            }),
          );
        } catch {
          showModal({ type: 'connectivePluginNotInstalled' });
        }
      }
    },
    [initializeConnectivePlugin, navigate, showModal],
  );

  const handleDownloadDocumentToFileSystem = useCallback(
    async (document: DocumentEntity) => {
      trackEvent({
        category: Tracker.Category.Vault,
        action: Tracker.Action.DownloadedDocument,
        value: 1,
      });
      await withMessages(downloadDocumentToFileSystem(document))(
        documentsMessages.downloadDocument(document.fileNameWithoutExtension),
      );
      await markDocumentAsSeen({ documentIds: [document.id], isSeen: true });
      hideModal();
    },
    [downloadDocumentToFileSystem, markDocumentAsSeen, hideModal, trackEvent],
  );

  return {
    handleDeleteDocument,
    handleEditDocumentName,
    handleEditDocumentLabels,
    handleDocumentHistory,
    handleDownloadDocumentToFileSystem,
    handleCopyDocument,
    handleSendCommunication,
    handleDocumentSeen,
    handleCertifyDocument,
    isLoadingDownloadDocument,
    isLoadingCopyDocument,
    isLoadingDeleteDocument,
  };
};

const useMultipleDocumentOptions = () => {
  const { showModal, hideModal } = useModalStore();
  const { clearDocumentSelection } = useDocumentSelection();
  const { handleDeleteDocuments, isPending: isLoadingDeleteDocuments } = useHandleDeleteDocuments();
  const { hideDeleteDocumentConfirmation } = useDocumentDeleteStore();
  const { markDocumentAsSeen } = hooks.useMarkDocumentAsSeen();
  const { downloadDocumentToFileSystem, isPending: isLoadingDownloadDocuments } =
    extendedHooks.useDownloadDocumentToFileSystem();

  const handleSendCommunicationWithMultipleDocuments = useCallback(
    async (documents: DocumentEntity[]) => {
      showModal({ type: 'sendCommunication', documents });
    },
    [showModal],
  );

  const handleDeleteMultipleDocuments = useCallback(
    async (documents: DocumentEntity[]) => {
      if (hideDeleteDocumentConfirmation) {
        await handleDeleteDocuments(documents);
        clearDocumentSelection();
        hideModal();
      } else {
        showModal({ type: 'deleteDocuments', documents });
      }
    },
    [
      hideDeleteDocumentConfirmation,
      handleDeleteDocuments,
      clearDocumentSelection,
      hideModal,
      showModal,
    ],
  );

  const handleSeenMultipleDocuments = useCallback(
    (documents: DocumentEntity[], isSeen: boolean) => {
      markDocumentAsSeen({
        documentIds: documents.map((d) => d.id),
        isSeen,
      });
    },
    [markDocumentAsSeen],
  );

  const handleDownloadMultipleDocuments = useCallback(
    (documents: DocumentEntity[]) => {
      documents.forEach(async (doc) => {
        withMessages(downloadDocumentToFileSystem(doc))(
          documentsMessages.downloadDocument(doc.fileNameWithoutExtension),
        );
        markDocumentAsSeen({ documentIds: [doc.id], isSeen: true });
      });
    },
    [downloadDocumentToFileSystem, markDocumentAsSeen],
  );

  return {
    handleSendCommunicationWithMultipleDocuments,
    handleDeleteMultipleDocuments,
    handleDownloadMultipleDocuments,
    handleSeenMultipleDocuments,
    isLoadingDeleteDocuments,
    isLoadingDownloadDocuments,
  };
};

const useDocumentTableDocuments = ({
  params,
  enabled = true,
}: {
  params?: PagedDocumentsArgs['params'];
  enabled?: PagedDocumentsArgs['enabled'];
} = {}) => {
  const { activeSorting } = useDocumentSorting('vault');
  const { filters, searchValue, onlyUnseen } = useDocumentFiltering();
  const {
    params: { filter },
  } = useQueryParams();

  const getNotaryFilterValue = () => {
    if (!isNotaryFilter(filter)) {
      return null;
    }
    switch (filter) {
      case 'citizen':
        return { sources: [ApiDocumentSource.Shared] };
      case 'notary':
        return { sources: [ApiDocumentSource.Study] };
      case 'fodfin':
        return { sources: [ApiDocumentSource.Tuyeau] };
      case 'vlabel':
        return { sources: [ApiDocumentSource.Vlabel] };
      case 'all':
      default:
        return null;
    }
  };

  const filterParams = {
    ...getDocumentFilterParameters(filters),
    ...params,
  };

  return hooks.usePagedDocuments({
    params: {
      sort: `${activeSorting.id},${activeSorting.desc ? 'desc' : 'asc'}`,
      documentName: searchValue || undefined,
      ...getNotaryFilterValue(),
      ...filterParams,
      isSeen: onlyUnseen() ? false : undefined,
    },
    type: 'vaultList',
    enabled,
  });
};

export const documentsModule = {
  ...hooks,
  ...extendedHooks,
  useDocumentOptions,
  useDocumentTableDocuments,
  useHandleDeleteDocuments,
  useMultipleDocumentOptions,
};

export type DocumentsModule = typeof documentsModule;
